import {
  InformationMemorandumAPIDataMap,
  InformationMemorandumAPIPath,
} from '@containers/information-memorandum/models/information-memorandum';
import { InformationMemorandumDetail } from '@containers/information-memorandum/models/information-memorandum-detail';
import { InformationMemorandumMenu } from '@containers/information-memorandum/models/information-memorandum-menu';
import { InformationMemorandumHtmlPreview } from '@containers/information-memorandum/models/response/information-memorandum-html-preview';

import { axios } from '@core/axios';
import { DIPOBackBaseResponse, MessageResponse } from '@model/response/@base-response/dipo-back-base-response';
import { Locale } from '@model/type/local.type';
import { AxiosResponse } from 'axios';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export class InformationMemorandumService {
  private static basePath = 'im';

  public static getImDetail(imId: number | string): Observable<InformationMemorandumDetail> {
    const url = `/${this.basePath}/${imId}/meta`;
    return from(axios.get<DIPOBackBaseResponse<InformationMemorandumDetail>>(url)).pipe(map(({ data }) => data.data[0]));
  }

  public static getImMenu(imId: number | string): Observable<InformationMemorandumMenu> {
    const url = `/${this.basePath}/${imId}/menu`;
    return from(axios.get<DIPOBackBaseResponse<InformationMemorandumMenu>>(url)).pipe(map(({ data }) => data.data[0]));
  }

  public static submit(imId: number | string): Observable<MessageResponse> {
    const url = `/${this.basePath}/${imId}/submit`;
    return from(axios.post<MessageResponse>(url)).pipe(map(({ data }) => data));
  }

  public static getPreviewHtml(imId: number | string, lang: 'en' | 'th'): Observable<InformationMemorandumHtmlPreview> {
    const url = `/${this.basePath}/${imId}/preview/html`;
    return from(
      axios.get<DIPOBackBaseResponse<InformationMemorandumHtmlPreview>>(url, {
        params: { lang },
      })
    ).pipe(map(({ data }) => data.data[0]));
  }
  public static getPreviewPDF(imId: number | string, lang: 'en' | 'th'): Observable<AxiosResponse<Blob>> {
    const url = `/${this.basePath}/${imId}/preview`;
    return from(
      axios.post<Blob>(url, undefined, {
        responseType: 'blob',
        params: {
          lang,
        },
      })
    );
  }

  public static getPreviewHtmlPublicByImId(imId: number | string, lang: 'en' | 'th'): Observable<InformationMemorandumHtmlPreview> {
    const url = `/public/${this.basePath}/${imId}/html`;
    return from(
      axios.get<DIPOBackBaseResponse<InformationMemorandumHtmlPreview>>(url, {
        params: { lang },
      })
    ).pipe(map(({ data }) => data.data[0]));
  }

  public static getPreviewPDFPublicByImId(imId: number | string, lang: 'en' | 'th'): Observable<AxiosResponse<Blob>> {
    const url = `/public/${this.basePath}/${imId}/download`;
    return from(
      axios.get<Blob>(url, {
        responseType: 'blob',
        params: { lang },
      })
    );
  }

  public static postPreviewPartHtml<P extends InformationMemorandumAPIPath>(
    imId: number | string,
    path: P,
    previewData: { [key: string]: InformationMemorandumAPIDataMap[P] },
    lang: Locale
  ): Observable<InformationMemorandumHtmlPreview> {
    const url = `/${this.basePath}/${imId}/preview/${path}/html`;
    return from(
      axios.post<DIPOBackBaseResponse<InformationMemorandumHtmlPreview>>(url, previewData, {
        params: { lang },
      })
    ).pipe(map(({ data }) => data.data[0]));
  }

  public static putTranslate<P extends InformationMemorandumAPIPath>(
    imId: number | string,
    path: P,
    translateData: InformationMemorandumAPIDataMap[P]
  ): Observable<MessageResponse> {
    const url = `/${this.basePath}/${imId}/${path}/translate`;
    return from(axios.put<MessageResponse>(url, translateData)).pipe(map(({ data }) => data));
  }

  public static patchTranslateCancel<P extends InformationMemorandumAPIPath>(imId: number | string, path: P): Observable<MessageResponse> {
    const url = `/${this.basePath}/${imId}/${path}/translate-cancel`;
    return from(axios.patch<MessageResponse>(url)).pipe(map(({ data }) => data));
  }

  public static pathRequestEdit(imId: number | string): Observable<MessageResponse> {
    const url = `/${this.basePath}/${imId}/request-edit`;
    return from(axios.patch<MessageResponse>(url)).pipe(map(({ data }) => data));
  }

  public static sendToApprover(imId: number | string): Observable<MessageResponse> {
    const url = `/${this.basePath}/${imId}/sendToApprove`;
    return from(axios.patch<MessageResponse>(url)).pipe(map(({ data }) => data));
  }

  public static approve(imId: number | string): Observable<MessageResponse> {
    const url = `/${this.basePath}/${imId}/approve`;
    return from(axios.patch<MessageResponse>(url)).pipe(map(({ data }) => data));
  }

  public static requestToEdit(imId: number | string): Observable<MessageResponse> {
    const url = `/${this.basePath}/${imId}/requestToEdit`;
    return from(axios.patch<MessageResponse>(url)).pipe(map(({ data }) => data));
  }

  public static allowViewBySetRegulatorDate(imId: number | string): Observable<MessageResponse> {
    const url = `/${this.basePath}/${imId}/allow-set`;
    return from(axios.patch<MessageResponse>(url)).pipe(map(({ data }) => data));
  }
}
